const en = {
    common: {
        appName: "LearnOS",
        signIn: "Sign in",
        startFree: "Start Learning Free",
        getStarted: "Get started",
        tryFree: "Try it free",
        watchDemo: "Watch Demo",
        seeInAction: "See LearnOS In Action",
        cancel: "Cancel",
        delete: "Delete",
        retry: "Retry",
        loading: "Loading…",
        included: "Included",
        comingSoon: "Coming Soon",
        placeholder: "Placeholder",
    },
    nav: {
        problem: "Problem",
        howItWorks: "How it works",
        features: "Features",
        tour: "Tour",
        testimonials: "Testimonials",
        modes: "Reading Modes",
        rewards: "Rewards",
        groupLibrary: "Library",
        groupLearn: "Learn",
        groupAccount: "Account",
        library: "Library",
        analytics: "Analytics",
        learnDashboard: "Learn Dashboard",
        flashcards: "Flashcards",
        review: "Review",
        examMode: "Exam Mode",
        profile: "Profile",
        signOut: "Sign out",
        tagline: "learn faster",
        brandTag: "AI Learning OS",
        language: "Language",
        groupEarn: "Program",
        ambassador: "Ambassador",
        admin: "Admin",
        pricing: "Pricing",
        upgrade: "Upgrade",
    },
    landing: {
        heroBadge: "Your personal AI tutor",
        heroTitle1: "Upload Anything.",
        heroTitle2: "Learn Everything.",
        heroSubtitle: "LearnOS isn’t a PDF reader. It turns any book, PDF or script into summaries, flashcards, quizzes and mock exams — guided by a personal AI tutor that helps you remember more and pass your exams.",
        heroModesLabel: "4 built-in reading modes",
        heroModes: [
            { name: "RSVP", tag: "One word at a time" },
            { name: "Ticker", tag: "Teleprompter flow" },
            { name: "Guided", tag: "Highlight paces you" },
            { name: "Focus", tag: "Distraction-free" },
        ],
        trust1: "No credit card",
        trust2: "Any PDF, book or script",
        trust3: "Free to start",
        problem: {
            label: "The Problem",
            title: "Studying Hasn’t Changed In Decades.",
            body: "People still read hundreds of pages, highlight, forget most of it, and cram before exams. The result is wasted time, poor retention and constant frustration.",
            cards: [
                { title: "Read hundreds of pages", desc: "Hours of passive reading with little to show for it." },
                { title: "Highlight & forget", desc: "Highlighting feels productive — but it barely helps memory." },
                { title: "Cram & panic", desc: "Last-minute cramming that fades days after the exam." },
            ],
        },
        solution: {
            label: "The Solution",
            title: "Meet Your Personal AI Tutor.",
            body: "LearnOS doesn’t just help you read. It helps you understand, remember and apply knowledge — automatically.",
            pillars: [
                { title: "Understand", desc: "AI breaks down complex material into ideas you actually grasp." },
                { title: "Remember", desc: "Active recall and spaced repetition lock knowledge in for good." },
                { title: "Apply", desc: "Quizzes, exams and coaching turn knowledge into real ability." },
            ],
        },
        journey: ["Upload", "AI Summary", "Reading Modes", "Chat", "Flashcards", "Quiz", "Exam Mode", "Knowledge Gaps", "AI Coach", "Mastery"],
        how: {
            label: "How It Works",
            title: "From Document To Mastery In Four Steps",
            step: "Step",
            steps: [
                { title: "Upload Any Content", desc: "Books, PDFs, scripts, reports, research — anything you need to learn." },
                { title: "AI Understands It", desc: "Extracts structure, key ideas, definitions and relationships." },
                { title: "Study Smarter", desc: "Flashcards, quizzes, exam simulations and active recall workflows." },
                { title: "Master The Knowledge", desc: "Find weak areas, track progress and improve with an AI coach." },
            ],
        },
        features: {
            label: "Features",
            title: "A Complete AI Learning Platform — Not Just A Reader",
            body: "Summaries, a tutor, flashcards, quizzes, exams, gap detection and coaching — all in one place. Speed reading is just one tool inside it.",
            items: [
                { title: "AI Tutor", desc: "Your personal tutor for any document — ask anything and get answers grounded in the source." },
                { title: "AI Summaries", desc: "1-minute, 5-minute and detailed summaries of anything, instantly." },
                { title: "Chat With Documents", desc: "Have a real conversation with your books and PDFs — with page-cited answers." },
                { title: "Flashcards", desc: "Auto-generated cards that make you learn through active recall." },
                { title: "Quiz Generator", desc: "Test your understanding with quizzes built from your material." },
                { title: "Exam Mode", desc: "Timed mock exams that feel like the real thing." },
                { title: "Knowledge Gap Detection", desc: "Discover exactly which topics you still don’t understand." },
                { title: "Knowledge Extraction", desc: "Key concepts, definitions and relationships pulled out automatically." },
                { title: "AI Coach", desc: "Personalized recommendations based on your real progress." },
                { title: "Reading Analytics", desc: "Track retention, mastery and your whole learning journey." },
                { title: "Reading Modes", desc: "Read your way: RSVP, Ticker, Guided and Focus modes built in." },
                { title: "Speed Reading", desc: "Consume content dramatically faster — one tool in the system." },
            ],
        },
        readingModes: {
            label: "Reading Modes",
            title: "Four Ways To Read — Built In",
            body: "Most apps give you one way to read. LearnOS gives you four, so you can match the mode to the material.",
            modes: [
                { name: "RSVP Mode", desc: "Words flashed one at a time at your target speed — read faster with less eye movement." },
                { name: "Ticker Mode", desc: "Text glides across a fixed line like a teleprompter for smooth, steady pacing." },
                { name: "Guided Reading", desc: "The text stays still while a smart highlight paces you through it — calm and natural." },
                { name: "Focus Reading", desc: "Dims everything but the line you’re on, so you stay locked in and distraction-free." },
            ],
        },
        personas: {
            label: "Built For You",
            title: "Built For Students, Professionals & Lifelong Learners",
            items: [
                { title: "Students", desc: "Turn lectures and textbooks into active recall, flashcards and quizzes.", tags: ["Medical School", "Law School", "Business School"] },
                { title: "Exam Preparation", desc: "Mock exams, gap detection and an AI coach that targets your weak spots.", tags: ["Board exams", "Finals", "Certifications"] },
                { title: "Professionals", desc: "Upskill faster and retain more from dense material.", tags: ["Certifications", "Professional Training"] },
                { title: "Lifelong Learners", desc: "Read smarter and actually remember what matters to you.", tags: ["Books", "Courses", "Research"] },
            ],
        },
        comparison: {
            label: "The Difference",
            title: "Traditional Learning vs LearnOS",
            traditional: "Traditional",
            traditionalFlow: "Read → Forget",
            traditionalBullets: ["Manual highlighting", "Cramming before exams", "Low long-term retention"],
            learnos: "LearnOS",
            learnosFlow: "Read → Understand → Remember → Master",
            learnosBullets: ["AI tutor guidance", "Active recall + spacing", "Progress + gap detection"],
            positioningLabel: "Positioning",
            positioningTitle: "Other AI Tools Help You Read. LearnOS Helps You Pass.",
            positioning: [
                { from: "A research assistant", to: "A personal AI tutor" },
                { from: "Understand documents", to: "Pass your exams" },
                { from: "Information", to: "Long-term retention" },
                { from: "Reading", to: "Mastery" },
                { from: "Document processing", to: "A complete learning system" },
            ],
        },
        tour: {
            label: "Interactive Product Tour",
            title: "Click Through The Journey",
            body: "See how LearnOS turns a single document into a personal AI tutor.",
            steps: [
                { title: "Upload a PDF", caption: "Start with any document." },
                { title: "Get an AI Summary", caption: "Understand the key ideas in minutes." },
                { title: "Ask Questions", caption: "Chat directly with your material." },
                { title: "Generate Flashcards", caption: "Turn information into memory." },
                { title: "Take a Quiz", caption: "Test what you actually know." },
                { title: "Take a Mock Exam", caption: "Timed exam practice that feels real." },
                { title: "Find Knowledge Gaps", caption: "Discover what needs more attention." },
                { title: "Improve With AI Coach", caption: "Get personalized recommendations." },
            ],
        },
        social: {
            label: "What Learners Say",
            title: "Loved By People Who Learn A Lot",
            testimonials: [
                { role: "Student", quote: "I wish I had this years ago — my study time dropped in half." },
                { role: "Medical Student", quote: "The knowledge gaps view tells me exactly what to review before exams." },
                { role: "Law Student", quote: "Turning cases into drills made exam prep finally feel structured." },
                { role: "Entrepreneur", quote: "I can learn any topic fast without losing the important details." },
                { role: "Professional", quote: "Perfect for certifications — quizzes and coaching keep me consistent." },
            ],
        },
        finalCta: {
            title: "Stop Reading. Start Learning.",
            body: "Join the next generation of AI-powered learning.",
        },
        ambassadorBlock: {
            label: "Student Ambassador Program",
            title: "Invite friends. Earn recurring rewards.",
            body: "We invest in our community instead of expensive advertising. Share LearnOS with the people around you, earn recurring rewards as they learn, and become part of the journey.",
            point1: "Recurring rewards for every active member you bring",
            point2: "Unlock free Pro & Premium months as you grow",
            point3: "Become part of the LearnOS journey — not a salesperson",
            cta: "Become an Ambassador",
        },
        footer: { tagline: "upload · understand · remember · master" },
    },
    preview: {
        onePlatform: "one platform",
        step: "step",
        upload: { drop: "Drop a PDF, EPUB or paste a link", dragDrop: "Drag & drop your file", formats: "PDF · EPUB · DOCX · TXT · URL", parsed: "Parsed", parsing: "Parsing…" },
        summary: { label5: "5-min summary", title5: "5-Minute Summary", bullets: ["The core thesis in one line.", "Three forces that drive the outcome.", "What to do with it tomorrow."], tourBullets: ["Learning is a trainable skill, not a fixed trait.", "Retention comes from active recall and spaced practice, not re-reading.", "Small, consistent sessions outperform occasional cramming."], terms: ["building", "retention", "recall"], ask: "Ask a question about this summary" },
        speed: { wpm: "RSVP · 450 WPM", oneFeature: "one feature of many" },
        chat: { q1: "What’s the main argument?", a1: "Consistent small practice compounds into mastery.", tourQ1: "What’s the single biggest factor in retention?", tourA1: "Active recall — testing yourself rather than re-reading. Pair it with spacing for the strongest effect.", tourQ2: "Give me 3 questions to test myself." },
        flashcard: { question: "Question", answer: "Answer", q: "What is spaced repetition?", a: "Reviewing material at increasing intervals to fight the forgetting curve.", tapReveal: "Tap the card to reveal the answer", rate: "Rate recall: Again · Good · Easy", activeRecall: "Active recall", card: "Card" },
        quiz: { q: "Which boosts retention most?", tourQ: "Which method improves long-term retention the most?", options: ["Re-reading", "Active recall", "Highlighting"], tourOptions: ["Re-reading the chapter", "Active recall + spacing", "Highlighting key sentences"], question: "Question", pick: "Pick an answer to see instant feedback", correct: "Correct — active recall + spacing wins.", wrong: "Not quite — active recall + spacing is best." },
        gaps: { need: "topics need review", needMore: "topics need more attention", confident: "confident", topics: ["Building intervals", "Recall timing"], tourTopics: ["Building intervals", "Recall vs recognition", "Optimal session length"], reviewNow: "Review weak topics now" },        coach: { todayPlan: "Today’s plan", yourPlan: "Your plan for today", items: ["Review 8 due flashcards", "Re-quiz: Intervals (10 min)"], tourItems: ["Review 8 due flashcards (≈ 6 min)", "Re-quiz: Building intervals", "Read 1 section on session length"], streakLine: "Next session keeps your 12-day streak alive" },
        exam: { mock: "Mock Exam", timeLeft: "Time left", q: "Question 4 / 20", prompt: "Which method gives the strongest long-term retention?", options: ["Re-reading notes", "Active recall under time", "Highlighting key lines"], submit: "Submit exam", graded: "Auto-graded · instant score" },
        mastery: { streak: "12-day streak", mastered: "124 cards mastered", retention: "Retention 86%" },
    },
    auth: {
        login: {
            title: "Welcome back",
            subtitle: "Sign in to continue learning.",
            email: "Email",
            emailPlaceholder: "you@example.com",
            password: "Password",
            passwordPlaceholder: "Your password",
            submit: "Sign in",
            demo: "Use demo account",
            noAccount: "Don’t have an account?",
            signUp: "Sign up",
            invalid: "Invalid email or password",
            welcome: "Welcome back!",
        },
        register: {
            title: "Create your account",
            subtitle: "Start reading and learning faster today.",
            name: "Name",
            namePlaceholder: "Ada Lovelace",
            email: "Email",
            emailPlaceholder: "you@example.com",
            password: "Password",
            passwordPlaceholder: "At least 6 characters",
            submit: "Create account",
            haveAccount: "Already have an account?",
            signIn: "Sign in",
            pwShort: "Password must be at least 6 characters",
            created: "Account created — welcome!",
            failed: "Could not create account",
            invitedBy: "{name} invited you to LearnOS",
            refApplied: "Ambassador invite applied",
        },
    },
    rset: {
        title: "Reading settings",
        subtitle: "Adjust movement, appearance and highlighting",
        movement: "Movement",
        display: "Appearance",
        highlight: "Highlighting",
        granularity: "Unit",
        grain: { word: "Word", phrase: "Phrase", sentence: "Sentence" },
        autoScroll: "Auto-scroll",
        dim: "Dim the rest",
        readingFocus: "Reading focus",
        focusMode: { minimal: "Minimal", word: "Active word", trail: "Light trail" },
        trailLength: "Trail length",
        trailLen: { short: "Short", medium: "Medium", long: "Long" },
        textAlign: "Text alignment",
        alignLeft: "Left",
        alignJustify: "Justified",
        theme: "Mode",
        font: "Font",
        fontNote: { dyslexia: "Dyslexia", sans: "Sans", serif: "Serif" },
        size: "Size",
        width: "Text width",
        wNarrow: "Narrow",
        wStandard: "Standard",
        wWide: "Wide",
        lineHeight: "Line spacing",
        lhCompact: "Compact",
        lhStandard: "Standard",
        lhAiry: "Airy",
        reset: "Reset to defaults",
        activeWord: "Active word",
        activeWordHint: "Only the text colour of the current word changes — no box.",
        wordAnchor: "Word anchor",
        anchor: { off: "Off", "2": "2", "3": "3", "4": "4", "2l": "2 + last", "3l": "3 + last", auto: "Auto" },
        intensity: "Intensity",
        smartHighlights: "AI Smart Highlights",
        analyzing: "Analysing document …",
        noHighlightsYet: "No AI Smart Highlights have been created for this document yet.",
        noHighlights: "No highlights found.",
        generate: "Generate AI Smart Highlights",
        guidedDesc: "A colour trail paces you · the whole text stays visible",
        focusDesc: "Active sentence in focus · the rest gently dimmed",
        smartHint: "The AI highlights the most important passages. Choose what gets marked below.",
        presets: {
            label: "Quick pick",
            hint: "One click combines the matching highlight layers.",
            custom: "Custom",
            recommend: "Recommended for this document",
            apply: "Apply",
            firstReading: "First reading",
            revision: "Revision",
            medical: "Medical exam",
            law: "Law exam",
            business: "Business exam",
        },
        domainLabel: { medical: "Medical terms", legal: "Legal terms", business: "Business terms" },
        hlCat: {
            definitions: { label: "Definitions", desc: "Terms defined in the text — the foundation for understanding.", style: "Teal, solid underline" },
            concepts: { label: "Core concepts", desc: "The central ideas the topic revolves around.", style: "Blue, dashed underline" },
            exam_relevant: { label: "Exam relevant", desc: "Facts that exams frequently test.", style: "Amber, strong fill" },
            entities: { label: "Key entities", desc: "Names, places, organisations and proper nouns.", style: "Magenta, dotted underline" },
            domain: { label: "Domain terms", desc: "Field-specific terminology of your subject.", style: "Green, double underline" },
            numbers: { label: "Dates & numbers", desc: "Dates, quantities, dosages and key figures.", style: "Cyan, solid underline" },
            keywords: { label: "AI keywords", desc: "Further keywords detected by the AI.", style: "Grey outline box" },
        },
    },
    workspace: {
        hubTitle: "Your workspaces",
        hubSubtitle: "Group multiple sources into one subject — your AI professor knows them all at once.",
        new: "New workspace",
        defaultName: "My Library",
        home: "Home",
        backToHub: "Back to workspaces",
        emptyTitle: "Create your first workspace",
        emptyBody: "A workspace unifies lectures, books, guidelines and your own notes into ONE knowledge base. Chat, summarise and test yourself across every source.",
        sourcesTitle: "Sources in this workspace",
        sourcesEmptyTitle: "No sources yet",
        sourcesEmptyBody: "Upload a document or add an existing source from your library to get started.",
        quizMode: "Quiz",
        examMode: "Exam",
        loadFailed: "Could not load workspaces.",
        card: {
            sources: "{n} sources",
            cards: "{n} cards",
            edit: "Edit",
            delete: "Delete",
            deleteTitle: "Delete workspace?",
            deleteBody: "The workspace will be deleted. Your sources are NOT lost — they move back to your library.",
            deleteConfirm: "Delete",
            deleted: "Workspace deleted",
            deleteFailed: "Could not delete workspace.",
        },
        tabs: {
            chat: "Chat",
            compare: "Compare",
            summary: "Summary",
            cards: "Flashcards",
            quiz: "Quiz & Exam",
            sources: "Sources",
        },
        chat: {
            title: "AI Professor",
            acrossSources: "across {n} sources",
            empty: "Ask your AI professor anything — it knows every source in this workspace at once.",
            placeholder: "Ask a question across all sources …",
            needSource: "Add at least one ready source first.",
            suggestions: ["Summarise the core concepts", "Where do the sources disagree?", "Build an exam checklist"],
        },
        compare: {
            title: "Cross-source analysis",
            intro: "Compare all sources: what they agree on, where they complement each other, where they contradict — and which source explains what best.",
            needTwo: "Add at least two ready sources to compare across them.",
            analyze: "Analyse sources",
            reanalyze: "Re-analyse",
            done: "Analysis complete",
            failed: "Analysis failed.",
            outdated: "Sources changed — re-analyse for an up-to-date result.",
            consensus: "Consensus",
            complementary: "Complementary",
            contradictions: "Contradictions",
            noContradictions: "No genuine contradictions found between the sources.",
            coverage: "What each source explains best",
            gaps: "Gaps",
            studyPlan: "Study recommendation",
        },
        summary: {
            title: "Unified summary",
            empty: "Generate one unified summary across all sources in this workspace.",
            generate: "Generate summary",
            regenerate: "Regenerate",
            failed: "Summary failed.",
            types: { student: "Student", five_min: "5 min", one_min: "1 min", fifteen_min: "15 min", executive: "Executive", action: "Action" },
        },
        create: {
            title: "New workspace",
            editTitle: "Edit workspace",
            nameLabel: "Name",
            namePlaceholder: "e.g. Neurology summer term",
            descLabel: "Description (optional)",
            descPlaceholder: "What is this workspace about?",
            iconLabel: "Icon",
            colorLabel: "Colour",
            createBtn: "Create",
            saveBtn: "Save",
            created: "Workspace created",
            updated: "Workspace updated",
            failed: "Could not save workspace.",
            nameRequired: "Please enter a name.",
        },
        addSource: {
            button: "From library",
            title: "Add a source",
            search: "Search documents …",
            add: "Add",
            added: "Added {title}",
            failed: "Could not add source.",
            inWorkspace: "In workspace",
            none: "No documents found.",
        },
    },
    library: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
        subtitle: "Your knowledge library — {count} {items}.",
        itemOne: "item",
        itemOther: "items",
        continueReading: "Continue reading",
        allDocuments: "All documents",
        search: "Search titles…",
        sortRecent: "Most recent",
        sortTitle: "Title A–Z",
        sortProgress: "Progress",
        sortLength: "Length",
        emptyTitle: "Your library is empty",
        emptyBody: "Upload a PDF, EPUB, DOCX or paste a link — or try a sample to see the magic in 30 seconds.",
        trySample: "Try a sample",
        noMatch: "No documents match “{query}”.",
        loadFailed: "Could not load your library",
        deleted: "Deleted",
        deleteFailed: "Delete failed",
        sampleAdded: "Sample added — let’s read it together",
        sampleFailed: "Could not load sample",
        processingReady: "“{title}” is ready to read",
        processingFailed: "A document could not be processed",
    },
    upload: {
        addContent: "Add content",
        title: "Add to your library",
        tabFile: "File",
        tabUrl: "URL",
        tabText: "Text",
        dropTitle: "Drop a file or click to browse",
        dropHint: "PDF, EPUB, DOCX, TXT, MD · up to 100MB",
        urlLabel: "Article / web page URL",
        urlPlaceholder: "https://example.com/article",
        importArticle: "Import article",
        titleLabel: "Title (optional)",
        titlePlaceholder: "My notes",
        pasteLabel: "Paste text",
        pastePlaceholder: "Paste any content here…",
        addText: "Add text",
        added: "Added “{title}”",
        imported: "Imported “{title}”",
        processing: "Uploaded “{title}” — extracting text in the background…",
        uploadFailed: "Upload failed",
        urlFailed: "Could not import URL",
        textFailed: "Could not add text",
        pasteMore: "Please paste more text",
    },
    card: {
        resume: "Resume",
        read: "Read",
        reread: "Re-read",
        study: "Study & remember",
        processing: "Processing…",
        processingHint: "Extracting text — large files take a moment.",
        ocrHint: "Scanned document — reading pages with AI…",
        ocrProgress: "AI reading page {done} of {total}…",
        failed: "Failed",
        failedHint: "We couldn’t process this file. Try uploading it again.",
        retrying: "Retrying — extracting text again…",
        deleteTitle: "Delete this document?",
        deleteBody: "This permanently removes the document and its reading progress.",
        deleteConfirm: "Delete",
        cancel: "Cancel",
    },
    profile: {
        title: "Profile",
        subtitle: "Manage your account and plan.",
        signOut: "Sign out",
        planSuffix: "plan",
        library: "library",
        hours: "hours",
        streak: "streak",
        plans: "Plans",
        current: "current",
        language: "Language",
        languageHelp: "Choose the language for the interface and new AI content.",
        viewPlans: "View plans & pricing",
        manageBilling: "Manage billing",
        upgradeCta: "Unlock unlimited learning",
        upgradeBody: "Upgrade to Pro or Premium for unlimited uploads, AI and exam tools.",
        tiers: [
            { name: "Free", desc: "Experience the value", features: ["Limited uploads", "AI summaries", "Basic speed reading"] },
            { name: "Pro", desc: "For most students & professionals", features: ["Unlimited everything", "Exam mode", "AI coach"] },
            { name: "Premium", desc: "The complete AI Tutor", features: ["Advanced retention", "Knowledge gap detection", "Priority AI"] },
        ],
    },
    pricing: {
        badge: "Pricing",
        title: "Choose your learning plan",
        subtitle: "Start free. Upgrade when you're ready to learn without limits.",
        perMonth: "/ month",
        free: "Free",
        mostPopular: "Most popular",
        completeTutor: "The complete AI Tutor",
        currentPlan: "Current plan",
        cancelAnytime: "Cancel anytime",
        plusPrevious: "Everything in Pro, plus:",
        backToApp: "Back to app",
        cta: { startFree: "Start free", upgradePro: "Upgrade to Pro", upgradePremium: "Upgrade to Premium", current: "Your current plan", signInToUpgrade: "Sign in to upgrade" },
        notConfigured: { title: "Billing not configured yet", body: "Payments go live soon. Explore every plan now — secure checkout opens once billing is enabled." },
        states: { processing: "Confirming your payment…", success: "Welcome aboard! Your plan is now active.", cancelled: "Checkout cancelled — you have not been charged." },
        tiers: {
            free: { name: "Free", tagline: "Experience the value", features: ["Limited uploads", "AI summaries", "Basic speed reading", "Limited chat with documents", "Limited flashcards", "Limited quizzes", "Basic analytics"] },
            pro: { name: "Pro", tagline: "For most students & professionals", features: ["Unlimited uploads", "Unlimited AI summaries", "Unlimited speed reading", "Unlimited chat with documents", "Unlimited knowledge extraction", "Full analytics", "Flashcards", "Quiz generator", "Exam mode", "AI coach"] },
            premium: { name: "Premium", tagline: "For serious exam preparation", features: ["Advanced retention tracking", "Knowledge gap detection", "Advanced AI coach", "Priority AI processing", "Learning paths (soon)", "Collaboration features (soon)", "Study groups (soon)", "Team features (soon)"] },
        },
    },
    ambassador: {
        badge: "Ambassador Program",
        title: "LearnOS Ambassador Program",
        subtitle: "We'd rather invest in our community than spend on expensive ads. Share LearnOS, help others learn — and get rewarded.",
        note: "You're not a salesperson — you're part of the journey. Thank you for helping LearnOS grow.",
        yourCode: "Your invite code",
        yourLink: "Your invite link",
        copyCode: "Copy code",
        copyLink: "Copy link",
        copied: "Copied to clipboard",
        howItWorks: "How it works",
        steps: [
            { title: "Share your link", desc: "Invite friends, classmates or colleagues to LearnOS." },
            { title: "They learn with LearnOS", desc: "When they upgrade to a paid plan, they become an active member." },
            { title: "You get rewarded", desc: "Earn recurring rewards and unlock free months." },
        ],
        stats: { total: "Total invited", active: "Active members", pending: "Pending" },
        rewardsTitle: "Your rewards",
        perReferral: "{amount} € per active member / month",
        estimate: "Estimated monthly",
        approved: "Approved monthly",
        paidOut: "Total paid out",
        pendingReview: "Awaiting review",
        milestonesTitle: "Reward milestones",
        earned: "Earned",
        locked: "Locked",
        proMonth: "1 month Pro free",
        premiumMonth: "1 month Premium free",
        milestoneProgress: "{count} / {target} active members",
        nextReward: "{remaining} more active members to earn {reward}",
        allUnlocked: "Amazing — you've unlocked every milestone!",
        membersTitle: "Your invited members",
        member: "Member",
        status: "Status",
        joined: "Joined",
        membersEmpty: "No invites yet. Share your link to get started.",
        statuses: { pending: "Pending", active: "Active", rejected: "Rejected" },
        payoutsTitle: "Your payouts",
        payoutsEmpty: "No payouts yet — keep sharing!",
        amount: "Amount",
        period: "Period",
    },
    admin: {
        title: "Admin",
        subtitle: "LearnOS operations control room.",
        notAuthorizedTitle: "Not authorized",
        notAuthorizedBody: "You don't have access to the admin area.",
        tabs: { overview: "Overview", users: "Users", referrals: "Referrals", payouts: "Payouts" },
        overview: { totalUsers: "Total users", free: "Free", pro: "Pro", premium: "Premium", paying: "Paying", mrr: "Estimated MRR", referrals: "Referrals", activeReferrals: "Active", growth: "Referral growth · 14 days", topAmbassadors: "Top ambassadors", noAmbassadors: "No ambassadors yet.", pendingPayouts: "Pending payouts", activeMembers: "active" },
        users: { search: "Search by name, email or code…", name: "Name", email: "Email", plan: "Plan", paying: "Paying", joined: "Joined", actions: "Actions", markPaying: "Mark paying", unmark: "Unmark", yes: "Yes", no: "No", updated: "User updated", empty: "No users found.", createPayout: "Create payout" },
        referrals: { ambassador: "Ambassador", member: "Member", status: "Status", commission: "Commission", actions: "Actions", approve: "Approve", reject: "Reject", updated: "Referral updated", empty: "No referrals yet.", filterAll: "All", filterActive: "Active", filterPending: "Pending" },
        payouts: { ambassador: "Ambassador", amount: "Amount", period: "Period", status: "Status", actions: "Actions", approve: "Approve", markPaid: "Mark paid", reject: "Reject", created: "Payout created", updated: "Payout updated", empty: "No payouts yet." },
        commissionStatus: { none: "None", eligible: "Eligible", approved: "Approved", rejected: "Rejected" },
        payoutStatus: { pending: "Pending", approved: "Approved", paid: "Paid", rejected: "Rejected" },
    },
    billing: {
        notConfigured: "Billing not configured yet",
        comingSoon: "Billing coming soon",
        manage: "Manage billing",
        viewPlans: "View plans",
        upgrade: "Upgrade",
    },
    explain: {
        title: "Quick explain",
        loading: "Understanding this…",
        error: "Couldn't explain this right now.",
        retry: "Try again",
        definition: "Definition",
        simply: "In simple terms",
        whyMatters: "Why it matters",
        examRelevance: "In exams",
        memoryTip: "Memory tip",
        related: "Related concepts",
        inPassage: "In this passage",
        save: "Save",
        saved: "Saved",
        note: "Note",
        notePlaceholder: "Add a note about this concept…",
        noteSaved: "Note saved",
        saveNote: "Save note",
        flashcard: "Flashcard",
        addedFlashcard: "Added to flashcards",
        askAI: "Ask AI",
        revisited: "Revisited {n}×",
        askQuestion: "Explain this concept in the context of this document: \"{term}\"",
        selectionHint: "Select any text to explain it",
    },
    learningProfile: {
        title: "Concepts you're exploring",
        subtitle: "Terms you looked up while reading. Revisit the ones that keep coming back.",
        empty: "Click any highlighted term while reading for an instant explanation. The concepts you explore will show up here.",
        saved: "{n} saved terms",
        revisit: "may need revision",
        frequently: "Frequently explored",
        suggestedReview: "Suggested for review",
    },
    glossary: {
        nav: "My Glossary",
        title: "My Glossary",
        subtitle: "Your personal concept library — every term you saved while reading.",
        count: "{n} concepts",
        search: "Search concepts, notes, documents…",
        filters: { all: "All", recent: "Recently saved", revisited: "Most revisited", review: "Needs review", understood: "Understood", flashcards: "In flashcards" },
        allDocuments: "All documents",
        allDomains: "All domains",
        status: { new: "New", review: "Review", understood: "Understood" },
        domain: { medical: "Medical", legal: "Legal", business: "Business", general: "General" },
        revisitsLabel: "{n}× revisited",
        savedOn: "Saved {date}",
        inFlashcards: "In flashcards",
        fields: { definition: "Definition", simply: "In simple terms", whyMatters: "Why it matters", examRelevance: "In exams", memoryTip: "Memory tip", related: "Related concepts", source: "Source" },
        actions: { open: "Open", openDoc: "Open document", jump: "Jump to passage", ask: "Ask AI", addFlashcard: "Add to flashcards", added: "In flashcards", note: "Note", notePlaceholder: "Your note about this concept…", saveNote: "Save note", remove: "Remove" },
        noteSaved: "Note saved",
        statusUpdated: "Status updated",
        addedFlashcard: "Added to flashcards",
        removed: "Removed from glossary",
        error: "Something went wrong.",
        empty: { title: "Build your personal concept library", body: "Save important terms while reading and they'll be collected here for later review.", cta: "Open a document and start exploring" },
        noResults: "No concepts match your filters.",
        tour: { title: "My Glossary", body: "Every concept you save while reading is collected here for later review — revisit it, add it to flashcards, or jump back to the source." },
    },
    onboarding: {
        badge: "Getting started",
        skip: "Skip",
        back: "Back",
        next: "Next",
        finish: "Finish",
        restart: "Restart tour",
        restartHelp: "Replay the guided tour of every LearnOS feature.",
        guide: "LearnOS Guide",
        stepOf: "Step {c} of {total}",
        steps: [
            { title: "Welcome to LearnOS", body: "Your personal AI tutor. Upload your own documents and learn faster, understand more, remember longer — and pass your exams." },
            { title: "Start however you like", body: "There is no wrong workflow. Pick the path that fits how you learn — and switch anytime.", chips: ["AI Summary", "Chat", "Speed Read", "Flashcards", "Quiz", "Exam Mode"] },
            { title: "Bring your own material", body: "Upload a file, paste a link, or drop in raw text. LearnOS turns it into an interactive learning space.", chips: ["PDF", "EPUB", "DOCX", "TXT", "Website", "Paste text"] },
            { title: "Grasp the gist in seconds", body: "Generate a summary at the depth you need. It's completely optional — skip it whenever you like.", chips: ["1 min", "5 min", "15 min", "Executive", "Student", "Action"] },
            { title: "Ask, don't just read", body: "Question your document instantly — no need to read it first. The AI already understands it and answers with sources.", chips: ["What's the key chapter?", "Explain this concept", "Give me examples", "Compare two ideas"] },
            { title: "Read your way", body: "Four focused reading modes, switchable anytime. Speed reading is just one option — never a requirement.", chips: ["RSVP", "Ticker", "Guided", "Focus"] },
            { title: "Attention, guided", body: "The AI highlights key concepts, definitions, exam-relevant facts and keywords — so your focus lands where it matters." },
            { title: "Understand any concept instantly", body: "Click a highlight for a simple explanation, exam relevance, a memory tip and related ideas — or ask the AI and save it.", chips: ["Definition", "Simple", "Exam relevance", "Memory tip", "Ask AI", "Save"] },
            { title: "Your personal concept library", body: "Every saved concept lives in My Glossary. Review later, add notes, create flashcards, or jump back to the source." },
            { title: "Flashcards, made for you", body: "Auto-generated from your material and ready for spaced review — so it actually sticks." },
            { title: "Test yourself", body: "Generate quizzes from your own documents and check what you really understood." },
            { title: "Rehearse the real thing", body: "Practice realistic exams and uncover the areas that still need work." },
            { title: "Guidance that's personal", body: "Your AI Coach gives learning recommendations tailored to how you're progressing." },
            { title: "Know what to review", body: "LearnOS gently surfaces concepts that may need another look — from your actual learning behavior, not guesswork." },
            { title: "See your progress", body: "Track reading time, speed, progress and your preferred reading mode over time." },
            { title: "Grow the community", body: "Invite friends and earn recurring rewards while helping LearnOS grow." },
            { title: "Plans that fit", body: "Start free and upgrade only if you need more. No pressure, ever.", chips: ["Free", "Pro", "Premium"] },
            { title: "You're all set", body: "You don't need every feature. Some just summarize and chat; others speed-read, drill flashcards or run exams. LearnOS adapts to you — explore and build your own way of learning." },
        ],
    },
    reader: {
        back: "Back to library",
        words: "words",
        loadFailed: "Could not load document",
        aiTools: "AI",
        modes: {
            rsvp: { label: "RSVP", hint: "RSVP Mode — word-by-word speed reading" },
            ticker: { label: "Ticker", hint: "Ticker Mode — moving text stream" },
            focus: { label: "Focus", hint: "Focus Reading — guided line-by-line" },
            guided: { label: "Guided", hint: "Guided Reading — the whole page stays still while an AI reading guide moves through it" },
        },
    },
    panels: { summaries: "Summaries", chat: "Chat", knowledge: "Knowledge" },
    summaries: {
        title: "AI Summaries",
        auto: "Auto",
        generate: "Generate {label} summary",
        regenerate: "Regenerate",
        empty: "Pick a summary length to distill this document in seconds.",
        error: "Could not generate summary",
        types: { one_min: "1 min", five_min: "5 min", fifteen_min: "15 min", executive: "Executive", student: "Student", action: "Action" },
    },
    chat: {
        title: "Chat with the book",
        empty: "Ask anything about this document. Answers cite the exact passages.",
        placeholder: "Ask about this document…",
        sending: "Reading the sources…",
        sendHint: "to send",
        newlineHint: "Shift+Enter for newline",
        error: "Chat failed",
        errorMsg: "_Sorry, I couldn't answer that. Please try again._",
        suggestions: [
            "What is the main message?",
            "Give me the key lessons.",
            "Where does it discuss the core idea?",
            "Challenge the author's argument.",
        ],
        quick: {
            simpler: "Explain simpler",
            deeper: "Go deeper",
            examples: "Give examples",
            examQ: "Make exam question",
            flashcard: "Save as flashcard",
        },
        quickPrompt: {
            simpler: "Explain your last answer more simply, in clear everyday words.",
            deeper: "Go deeper on your last answer — with detail and background.",
            examples: "Give me concrete examples for your last answer.",
            examQ: "Write a typical exam question about your last answer and answer it briefly.",
        },
        card: {
            preview: "Flashcard preview",
            creating: "Creating flashcard …",
            front: "Front",
            back: "Back",
            save: "Save flashcard",
            cancel: "Cancel",
            saved: "Flashcard saved",
            duplicate: "This card already exists in your deck.",
            failed: "Could not create flashcard.",
        },
    },
    overview: {
        button: "Overview",
        title: "Highlights overview",
        subtitle: "All marked passages — for quick review and jumping.",
        all: "All",
        empty: "No highlights to show yet. Enable layers or generate AI Smart Highlights.",
        count: "{n} passages",
        jump: "Jump to passage",
        explain: "Explain",
        noMatch: "No matches in this category.",
        position: "Position {n}%",
    },
    knowledge: {
        title: "Knowledge",
        empty: "Extract the highest-signal insights, actions, quotes, concepts and mental models.",
        generate: "Extract knowledge",
        error: "Extraction failed",
        insights: "Key Insights",
        actions: "Action Items",
        quotes: "Quotes",
        concepts: "Concepts",
        models: "Mental Models",
    },
    learn: {
        title: "Learning",
        subtitle: "Read → Understand → Remember → Master.",
        dueTitle: "{n} cards due for review",
        dueSubtitle: "Spaced repetition keeps knowledge from fading.",
        startReview: "Start review",
        statFlashcards: "Flashcards",
        statMastered: "Mastered",
        statQuizzes: "Quizzes",
        statAvgQuiz: "Avg quiz",
        growth: "Knowledge growth",
        states: { new: "New", learning: "Learning", familiar: "Familiar", mastered: "Mastered" },
        achievements: "Achievements",
        masteryByDoc: "Mastery by document",
        masteryEmpty: "Read a document and create flashcards to start building mastery.",
    },
    study: { mode: "Study mode", exam: "Exam", tabRecap: "Recap", tabFlashcards: "Flashcards", tabQuiz: "Quiz", tabReflect: "Reflect", loadFailed: "Could not load" },
    examPage: { mode: "Exam mode", mockTitle: "Timed mock exam", mockDesc: "Generated only from this document. The exam auto-submits when the timer runs out.", loadFailed: "Could not load" },
    review: {
        title: "Spaced repetition review",
        allCaughtTitle: "All caught up!",
        allCaughtBody: "No cards are due right now. Create more flashcards or come back later.",
        backToLearning: "Back to Learning",
        completeTitle: "Review complete!",
        completeBodyGreat: "You reviewed {n} cards. Great recall!",
        completeBodyNeed: "You reviewed {n} cards. {again} need another look soon.",
        loadFailed: "Could not load reviews",
    },
    analytics: {
        title: "Reading Analytics",
        subtitle: "Your learning momentum at a glance.",
        streak: "Streak",
        hoursRead: "Hours read",
        avgWpm: "Avg WPM",
        fastestWpm: "Fastest WPM",
        inLibrary: "In library",
        completed: "Completed",
        wordsRead: "Words read",
        retention: "Retention",
        daysSuffix: "days",
        hoursSuffix: "h",
        wpmTrend: "WPM trend (14 days)",
        minutesTrend: "Minutes read (14 days)",
        readingStyle: "Reading style",
        readingStyleDesc: "Which reading mode you actually use most.",
        prefers: "Prefers {mode}",
        readingStyleEmpty: "Start reading in any mode to see your style here.",
        sessions: "sessions",
        minShort: "min",
        activity: "Activity (last 7 weeks)",
        less: "less",
        more: "more",
        achievements: "Achievements",
        badges: { first: "First Read", streak3: "3-Day Streak", fast: "Speed 500+", finisher: "Finisher", scholar: "Scholar" },
    },
    docpicker: {
        study: { title: "Flashcards", subtitle: "Pick a book to build and study its flashcards.", emptyTitle: "Build your active recall", emptyBody: "Flashcards turn any document into spaced repetition — the fastest way to make knowledge stick. Add a book to get started." },
        exam: { title: "Exam Mode", subtitle: "Pick a book to sit a timed mock exam generated from it.", emptyTitle: "Practice under real conditions", emptyBody: "Timed mock exams built from your own material show you whether you're truly ready. Add a book to get started." },
        cards: "cards",
        quizzes: "quizzes",
        ctaStudy: "Study cards",
        ctaExam: "Start exam",
        goToLibrary: "Go to Library",
    },
    deck: {
        generate: "Generate flashcards",
        generateMore: "Generate more cards",
        generateBtn: "Generate",
        types: { basic: "Q&A", concept: "Concept", fill_blank: "Fill blank", reasoning: "Reasoning" },
        cardTypes: "Card types",
        difficulty: "Difficulty",
        count: "Count",
        diffs: { beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced" },
        pickType: "Pick at least one card type",
        created: "Created {n} flashcards",
        genFailed: "Generation failed",
        cards: "cards",
        studyThese: "Study these",
        backToDeck: "Back to deck",
        reviewComplete: "Review session complete",
        editTitle: "Edit flashcard",
        front: "Front",
        back: "Back",
        saved: "Saved",
        saveFailed: "Save failed",
        regenerated: "Card regenerated",
        regenFailed: "Regenerate failed",
    },
    quiz: {
        questionTypes: "Question types",
        difficulty: "Difficulty",
        questions: "Questions",
        timeLimit: "Time limit",
        startQuiz: "Start quiz",
        startExam: "Start exam",
        types: { mcq: "Multiple choice", true_false: "True / False", open: "Open question", scenario: "Scenario", case_study: "Case study", exam: "Exam question" },
        diffs: { beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced" },
        craftingQuiz: "Crafting your quiz…",
        craftingExam: "Crafting your exam…",
        question: "Question {i} / {n}",
        answered: "{n} answered",
        back: "Back",
        next: "Next",
        submit: "Submit",
        correctCount: "{correct} / {total} correct",
        recommendedReview: "Recommended review",
        noWeak: "No weak areas — excellent!",
        done: "Done",
        yourAnswer: "your answer",
        feedback: "Feedback",
        modelAnswer: "model answer",
        correctAnswer: "Correct answer:",
        typeAnswer: "Type your answer…",
        trueLabel: "True",
        falseLabel: "False",
        genFailed: "Quiz generation failed",
        submitFailed: "Could not submit",
        score: "score",
    },
    fcReview: {
        card: "Card {i} / {n}",
        question: "Question",
        answer: "Answer",
        reveal: "Reveal answer",
        space: "(space)",
        hint: "Hint",
        again: "Again",
        hard: "Hard",
        good: "Good",
        easy: "Easy",
    },
    coach: {
        title: "AI Learning Coach",
        focusTopics: "Focus topics",
        nextSteps: "Next steps",
        empty: "Read and review a bit more, then your coach will have insights for you.",
    },
    reflection: {
        intro: "Reflect to remember. Writing in your own words boosts retention dramatically.",
        placeholder: "Write your reflection…",
        loadFailed: "Could not load reflection questions",
    },
    scores: { reading: "Reading", understanding: "Understanding", retention: "Retention", mastery: "Mastery" },
    completion: {
        title: "Document complete!",
        body: "You finished {title}.",
        faster: "You read it about {min} min faster than average.",
        words: "words",
        yourSpeed: "your speed",
        timeSaved: "time saved",
        wpm: "wpm",
        min: "min",
        studyToRemember: "Study to remember",
        readSummary: "Read the summary",
        ask: "Ask",
        library: "Library",
    },
    achievements: {
        earned: "earned",
        items: {
            first_book: { label: "First Book Completed", desc: "Finish your first document" },
            streak10: { label: "10-Day Streak", desc: "Read 10 days in a row" },
            cards100: { label: "100 Flashcards Mastered", desc: "Master 100 cards" },
            quiz90: { label: "90% Quiz Score", desc: "Score 90%+ on a quiz" },
            words100k: { label: "100,000 Words Read", desc: "Read 100k words" },
            builder: { label: "Knowledge Builder", desc: "Create 50 flashcards" },
            fast_learner: { label: "Fast Learner", desc: "Average 400+ WPM" },
            master_reader: { label: "Master Reader", desc: "Reach 80% mastery on a document" },
        },
    },
    detail: {
        label: "Depth",
        quick: "Quick", standard: "Standard", expert: "Expert",
        quickHint: "Short & direct", standardHint: "Clear & structured",
        expertHint: "University level — thorough, exam-oriented",
    },
    pro: {
        deepDive: "Deep dive",
        improve: "Improve", harder: "Make harder",
        fromWeak: "Study weak spots", fromWeakDesc: "Generate cards from your weak topics",
        noWeak: "No weak topics yet — take a quiz first.",
        weakGenerated: "Created {n} cards on weak topics",
        whatCorrect: "What you got right", whatMissing: "What was missing", whatWrong: "What was wrong",
        examinerNote: "Examiner's note", howToImprove: "How to improve",
        commonMistake: "Common mistake", advice: "Learning tip",
    },
};

export default en;
